import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Btn, Pill, Modal, inputCls } from "@/dashboard/ui";
import { BarChart3, Sparkles, Download, FileText, CheckCircle2 } from "lucide-react";

const STATE_TONE = { connected: "green", partial: "amber", needs_setup: "grey", unavailable: "outline" };
const STATE_LABEL = { connected: "Connected", partial: "Partial", needs_setup: "Needs setup", unavailable: "Unavailable" };
const CHANNELS = [
  ["landing_pages", "Landing Pages"], ["crm", "CRM Leads"], ["appointments", "Appointments"],
  ["email", "Email"], ["whatsapp", "WhatsApp"], ["social", "Social"],
  ["seo", "SEO / Search Console"], ["google_ads", "Google Ads"], ["meta_ads", "Meta Ads"],
];

function num(v) {
  return v === null || v === undefined ? "—" : v;
}

function ChannelCard({ id, label, ch, testid }) {
  const metrics = Object.entries(ch).filter(([k, v]) => !["state", "note", "utm", "by_source"].includes(k));
  return (
    <Card className="p-5" data-testid={`analytics-channel-${id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-bold text-[#111111]">{label}</p>
        <Pill tone={STATE_TONE[ch.state]} data-testid={`analytics-state-${id}`}>{STATE_LABEL[ch.state]}</Pill>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
        {metrics.map(([k, v]) => (
          <p key={k} className="text-xs text-neutral-600" data-testid={`analytics-metric-${id}-${k}`}>
            <span className="font-bold text-[#111111]">{typeof v === "object" ? "—" : num(v)}</span> {k.replace(/_/g, " ")}
          </p>
        ))}
      </div>
      {ch.note && <p className="mt-2 text-[11px] text-neutral-400">{ch.note}</p>}
    </Card>
  );
}

export default function AnalyticsPanel() {
  const [range, setRange] = useState("30");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportBusy, setReportBusy] = useState(false);

  const params = useMemo(() => {
    if (range === "custom") return { from, to };
    const t = new Date();
    const f = new Date();
    f.setDate(f.getDate() - (parseInt(range) - 1));
    return { from: f.toISOString().slice(0, 10), to: t.toISOString().slice(0, 10) };
  }, [range, from, to]);

  const load = () => {
    api.get("/marketing/analytics/overview", { params }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/marketing/analytics/reports").then((r) => setReports(r.data.reports)).catch(() => {});
  };
  useEffect(() => { if (range !== "custom" || (from && to)) load(); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateReport = async () => {
    setReportBusy(true);
    try {
      const { data: r } = await api.post("/marketing/analytics/report", { from_date: params.from, to_date: params.to });
      setReport(r);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReportBusy(false);
    }
  };

  const download = (r) => {
    const lines = [
      `# Savy Kerno marketing report`, `Range: ${r.range.from} → ${r.range.to}`, ``,
      `## Facts (from verified data)`, ...r.facts.map((f) => `- ${f}`), ``,
      `## Recommendations (AI advice — not results)`, ...r.recommendations.map((x) => `- ${x}`),
    ].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([lines], { type: "text/markdown" }));
    a.download = `marketing-report-${r.range.from}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-5" data-testid="analytics-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2" data-testid="analytics-range">
          {[["7", "7 days"], ["30", "30 days"], ["90", "90 days"], ["custom", "Custom"]].map(([id, l]) => (
            <button key={id} data-testid={`analytics-range-${id}`} onClick={() => setRange(id)}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${range === id ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>{l}</button>
          ))}
          {range === "custom" && (
            <>
              <input type="date" className={inputCls} value={from} data-testid="analytics-from" onChange={(e) => setFrom(e.target.value)} />
              <input type="date" className={inputCls} value={to} data-testid="analytics-to" onChange={(e) => setTo(e.target.value)} />
            </>
          )}
        </div>
        <Btn data-testid="analytics-generate-report" disabled={reportBusy} onClick={generateReport}>
          <Sparkles size={14} className="mr-1.5 inline" /> {reportBusy ? "Analysing…" : "AI report"}
        </Btn>
      </div>

      {!data ? <p className="py-8 text-center text-sm text-neutral-400">Loading…</p> : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7" data-testid="analytics-totals">
            {[["Leads", data.totals.leads], ["Bookings", data.totals.bookings], ["Emails sent", data.totals.emails_sent],
              ["WhatsApp sent", data.totals.wa_sent], ["Page views", data.totals.lp_views], ["Enquiries", data.totals.lp_submissions],
              ["Page bookings", data.totals.lp_bookings]].map(([l, v]) => (
              <Card key={l} className="p-3.5 text-center">
                <p className="text-xl font-extrabold text-[#111111]" data-testid={`analytics-total-${l.toLowerCase().replace(/\s+/g, "-")}`}>{v}</p>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400">{l}</p>
              </Card>
            ))}
          </div>

          <Card className="p-5" data-testid="analytics-trend">
            <p className="mb-3 text-xs font-bold uppercase tracking-wider text-neutral-500">Trend</p>
            {data.series.every((d) => !d.views && !d.leads && !d.bookings && !d.emails_sent) ? (
              <p className="py-6 text-center text-xs text-neutral-400" data-testid="analytics-trend-empty">No recorded activity in this range yet — the chart fills in as real events happen.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.series}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="leads" stroke="#111111" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="bookings" stroke="#6b5ce7" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="views" stroke="#10b981" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="emails_sent" stroke="#d97706" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {CHANNELS.map(([id, label]) => <ChannelCard key={id} id={id} label={label} ch={data.channels[id]} />)}
          </div>

          {Object.keys(data.channels.landing_pages.utm || {}).length > 0 && (
            <Card className="p-5" data-testid="analytics-utm">
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-neutral-500">Campaign attribution (UTM)</p>
              {Object.entries(data.channels.landing_pages.utm).map(([src, v]) => (
                <p key={src} className="text-xs text-neutral-600" data-testid={`analytics-utm-${src}`}>
                  <span className="font-semibold">{src}</span> — {v.views} views · {v.submissions} enquiries · {v.bookings} bookings
                </p>
              ))}
            </Card>
          )}

          <p className="text-[11px] text-neutral-400" data-testid="analytics-money-note">{data.money_note}</p>
          <p className="text-[11px] text-neutral-400">{data.data_note}</p>
        </>
      )}

      {reports.length > 0 && (
        <Card className="p-5" data-testid="analytics-reports">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-neutral-500">Saved AI reports</p>
          <div className="space-y-1.5">
            {reports.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2" data-testid={`analytics-report-${r.id}`}>
                <button className="flex items-center gap-1.5 text-xs font-medium text-neutral-700 hover:text-[#111111]" onClick={() => setReport(r)}>
                  <FileText size={13} /> {r.range.from} → {r.range.to} · {String(r.created_at).slice(0, 16).replace("T", " ")}
                </button>
                <button className="text-neutral-400 hover:text-[#111111]" title="Download" data-testid={`analytics-report-dl-${r.id}`} onClick={() => download(r)}><Download size={14} /></button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {report && (
        <Modal title={`AI report — ${report.range.from} → ${report.range.to}`} onClose={() => setReport(null)} testid="ai-report-modal">
          <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500"><CheckCircle2 size={13} /> Facts — from your verified data</p>
              <ul className="list-inside list-disc space-y-1 text-xs text-neutral-700" data-testid="ai-report-facts">
                {report.facts.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500"><Sparkles size={13} /> Recommendations — AI advice, not results</p>
              <ul className="list-inside list-disc space-y-1 text-xs text-neutral-700" data-testid="ai-report-recs">
                {report.recommendations.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
            <div className="flex justify-end">
              <Btn data-testid="ai-report-download" onClick={() => download(report)}><Download size={13} className="mr-1 inline" /> Download report</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
